import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Send,
  Loader2,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Zap,
  Coffee,
  Save,
  AlertTriangle,
  X,
} from "lucide-react";
import { notification, Modal } from "antd";
import api from "../../API/api";
import useFormDraft from "../../hooks/useFormDraft";

const GraduateFormPage = ({
  formId,
  onSubmitSuccess,
  onCancel,
  isUpdate = false,
  existingData = null,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [questions, setQuestions] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [visibleQuestions, setVisibleQuestions] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const surveyId = id || formId;

  const draftMeta = useMemo(() => ({ currentPage }), [currentPage]);

  const setDraftMeta = useCallback((meta) => {
    if (meta?.currentPage != null) {
      setCurrentPage(meta.currentPage);
    }
  }, []);

  const isAnswersEmpty = useCallback((ans) => {
    if (!ans || Object.keys(ans).length === 0) return true;
    return Object.values(ans).every((v) => {
      if (Array.isArray(v)) return v.length === 0;
      return v === "" || v == null;
    });
  }, []);

  const {
    markEdited,
    clearDraft,
    draftModals,
    draftBanner,
  } = useFormDraft({
    namespace: "inquerito",
    formId: surveyId,
    data: answers,
    setData: setAnswers,
    meta: draftMeta,
    setMeta: setDraftMeta,
    enabled: !(isUpdate && existingData),
    ready: !loading && questions.length > 0,
    isEmpty: isAnswersEmpty,
  });

  // Agrupa perguntas por seção (super_question)
  const groupQuestionsBySection = useCallback((questionsList, visibilityMap) => {
    const sections = [];
    const processedSuperQuestions = new Set();

    // Filtra apenas perguntas visíveis
    const visibleQuestionsList = questionsList.filter(q => visibilityMap[q.id] !== false);

    visibleQuestionsList.forEach((q) => {
      if (q.super_question && !processedSuperQuestions.has(q.super_question.id)) {
        processedSuperQuestions.add(q.super_question.id);
        sections.push({
          id: q.super_question.id,
          title: q.super_question.label,
          questions: visibleQuestionsList.filter(
            (q2) => q2.super_question?.id === q.super_question.id,
          ),
        });
      }
    });

    const standaloneQuestions = visibleQuestionsList.filter((q) => !q.super_question);
    standaloneQuestions.forEach((q) => {
      sections.push({
        id: q.id,
        title: q.label,
        questions: [q],
      });
    });

    return sections;
  }, []);

  // Avalia condições para determinar visibilidade das perguntas
  const evaluateConditions = useCallback((currentAnswers, conditionsList, allQuestions) => {
    const visibility = {};
    
    // Inicialmente todas as perguntas são visíveis
    allQuestions.forEach(q => {
      visibility[q.id] = true;
    });

    conditionsList.forEach(condition => {
      const sourceValue = currentAnswers[condition.source_question];
      const targetId = condition.target_question;
      
      if (!sourceValue) return;

      let shouldShow = false;

      switch (condition.operator) {
        case "equals":
          if (condition.option) {
            shouldShow = sourceValue === condition.option;
          } else if (condition.value !== null && condition.value !== undefined) {
            shouldShow = String(sourceValue) === String(condition.value);
          }
          break;
          
        case "not_equals":
          if (condition.option) {
            shouldShow = sourceValue !== condition.option;
          } else if (condition.value !== null && condition.value !== undefined) {
            shouldShow = String(sourceValue) !== String(condition.value);
          }
          break;
          
        case "greater_than":
          shouldShow = parseFloat(sourceValue) > parseFloat(condition.value);
          break;
          
        case "less_than":
          shouldShow = parseFloat(sourceValue) < parseFloat(condition.value);
          break;
          
        default:
          shouldShow = true;
      }

      if (!shouldShow) {
        visibility[targetId] = false;
      }
    });

    return visibility;
  }, []);

  // Busca dados do formulário
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // 1. Buscar perguntas do formulário
        const questionsResponse = await api.get(`/v1/builder/forms/${surveyId}/questions/`);
        const questionsData = questionsResponse.data;
        setQuestions(questionsData);

        // 2. Buscar condições do formulário
        let conditionsData = [];
        try {
          const conditionsResponse = await api.get(`/v1/builder/forms/${surveyId}/questions-conditions/`);
          conditionsData = conditionsResponse.data;
          setConditions(conditionsData);
        } catch (err) {
          if (err.response?.status !== 404) {
            console.error("Erro ao buscar condições:", err);
          }
          setConditions([]);
        }

        // 3. Inicializar respostas
        const initialAnswers = {};
        questionsData.forEach((q) => {
          if (existingData && existingData[q.id]) {
            initialAnswers[q.id] = existingData[q.id];
          } else if (q.type === "checkbox") {
            initialAnswers[q.id] = [];
          } else {
            initialAnswers[q.id] = "";
          }
        });
        setAnswers(initialAnswers);

        // 4. Avaliar visibilidade inicial
        const initialVisibility = evaluateConditions(initialAnswers, conditionsData, questionsData);
        setVisibleQuestions(initialVisibility);

      } catch (error) {
        console.error("Erro ao carregar dados:", error);
        notification.error({
          message: "Erro",
          description: "Não foi possível carregar o formulário.",
          placement: "topRight",
        });
      } finally {
        setLoading(false);
      }
    };

    if (surveyId) {
      fetchData();
    }
  }, [surveyId, existingData, evaluateConditions]);

  // Reavalia condições quando respostas mudam
  useEffect(() => {
    if (questions.length > 0 && conditions.length > 0) {
      const newVisibility = evaluateConditions(answers, conditions, questions);
      setVisibleQuestions(newVisibility);
      
      Object.entries(newVisibility).forEach(([questionId, isVisible]) => {
        if (!isVisible && answers[questionId]) {
          setAnswers(prev => {
            const newAnswers = { ...prev };
            delete newAnswers[questionId];
            return newAnswers;
          });
        }
      });
    }
  }, [answers, questions, conditions, evaluateConditions]);

  const handleInputChange = (questionId, value) => {
    markEdited();
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    if (errors[questionId]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  const handleCheckboxChange = (questionId, optionId, checked) => {
    const currentValues = answers[questionId] || [];
    let newValues;
    if (checked) {
      newValues = [...currentValues, optionId];
    } else {
      newValues = currentValues.filter((id) => id !== optionId);
    }
    handleInputChange(questionId, newValues);
  };

  const validatePage = (pageQuestions) => {
    const newErrors = {};
    let isValid = true;

    pageQuestions.forEach((question) => {
      if (visibleQuestions[question.id] === false) return;

      const answer = answers[question.id];
      if (question.required) {
        if (question.type === "checkbox") {
          if (!answer || answer.length === 0) {
            newErrors[question.id] = "Por favor, selecione pelo menos uma opção.";
            isValid = false;
          }
        } else if (!answer || answer === "") {
          newErrors[question.id] = "Este campo é obrigatório.";
          isValid = false;
        }
      }
      
      if (question.type === "number" && answer && !isNaN(answer)) {
        const numValue = parseFloat(answer);
        if (isNaN(numValue)) {
          newErrors[question.id] = "Por favor, insira um número válido.";
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const getVisibleSections = useCallback(() => {
    return groupQuestionsBySection(questions, visibleQuestions);
  }, [questions, visibleQuestions, groupQuestionsBySection]);

  const sections = getVisibleSections();
  const currentSection = sections[currentPage];
  const totalPages = sections.length;
  const progress = totalPages > 0 ? ((currentPage + 1) / totalPages) * 100 : 0;

  const nextPage = () => {
    if (validatePage(currentSection?.questions || [])) {
      markEdited();
      setCurrentPage((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const prevPage = () => {
    markEdited();
    setCurrentPage((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Preparar payload para o bulk create
  const prepareBulkPayload = () => {
    const visibleQuestionsList = questions.filter(q => visibleQuestions[q.id] !== false);
    const payload = [];

    for (const question of visibleQuestionsList) {
      const answer = answers[question.id];
      
      // Pula perguntas sem resposta
      if (!answer || (Array.isArray(answer) && answer.length === 0)) {
        continue;
      }

      const answerPayload = {
        question: question.id,
        text_answer: null,
        number_answer: null,
        date_answer: null,
        selected_options: [],
      };

      // Monta payload baseado no tipo da pergunta
      switch (question.type) {
        case "text":
        case "textarea":
          answerPayload.text_answer = String(answer);
          break;
        case "number":
          answerPayload.number_answer = parseFloat(answer);
          break;
        case "radio":
          answerPayload.selected_options = [answer];
          break;
        case "checkbox":
          answerPayload.selected_options = answer;
          break;
        default:
          answerPayload.text_answer = String(answer);
      }

      payload.push(answerPayload);
    }

    return payload;
  };

  // Submissão usando bulk create
  const submitForm = async () => {
    setSubmitting(true);
    setShowConfirmModal(false);

    try {
      const payload = prepareBulkPayload();

      if (payload.length === 0) {
        notification.warning({
          message: "Nenhuma resposta",
          description: "Por favor, responda pelo menos uma pergunta antes de submeter.",
          placement: "topRight",
        });
        setSubmitting(false);
        return;
      }

      // Endpoint de bulk create
      const response = await api.post(`/v1/builder/form-answers/${surveyId}/bulk/`, payload);

      notification.success({
        message: isUpdate ? "Actualizado!" : "Sucesso!",
        description: isUpdate
          ? "O formulário foi actualizado com sucesso."
          : "O formulário foi submetido com sucesso. Obrigado pela sua participação!",
        placement: "topRight",
        duration: 4,
      });

      await clearDraft();

      if (onSubmitSuccess) {
        onSubmitSuccess(answers);
      } else {
        setTimeout(() => {
          navigate("/empregabilidade");
        }, 2000);
      }
    } catch (error) {
      console.error("Erro ao submeter formulário:", error);
      
      let errorMessage = "Ocorreu um erro ao submeter o formulário. Tente novamente.";
      
      if (error.response?.status === 400) {
        errorMessage = error.response.data?.detail || "Dados inválidos. Verifique suas respostas.";
      } else if (error.response?.status === 401) {
        errorMessage = "Sua sessão expirou. Por favor, faça login novamente.";
      } else if (error.response?.status === 404) {
        errorMessage = "Formulário não encontrado.";
      }
      
      notification.error({
        message: isUpdate ? "Erro ao atualizar" : "Erro ao submeter",
        description: errorMessage,
        placement: "topRight",
        duration: 5,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Validar antes de mostrar o modal
  const handleSubmitClick = () => {
    const allVisibleQuestions = questions.filter(q => visibleQuestions[q.id] !== false);
    if (!validatePage(allVisibleQuestions)) {
      notification.warning({
        message: "Campos pendentes",
        description: "Por favor, preencha todos os campos obrigatórios.",
        placement: "topRight",
      });
      return;
    }
    setShowConfirmModal(true);
  };

  const renderQuestion = (question) => {
    if (visibleQuestions[question.id] === false) {
      return null;
    }

    const value = answers[question.id];
    const error = errors[question.id];

    switch (question.type) {
      case "text":
        return (
          <div className="group" key={question.id}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.label}
              {question.required && (
                <span className="text-red-400 ml-1 text-xs">*</span>
              )}
            </label>
            <div className="relative">
              <input
                type="text"
                value={value || ""}
                onChange={(e) => handleInputChange(question.id, e.target.value)}
                className={`w-full rounded-xl border-2 ${error ? "border-red-200 bg-red-50/30" : "border-gray-100 bg-gray-50/30"} px-5 py-4 text-base focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400 transition-all duration-200 hover:bg-white`}
                placeholder="Digite sua resposta..."
              />
              {value && !error && (
                <CheckCircle className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-400" />
              )}
            </div>
            {error && (
              <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                <AlertCircle size={12} /> {error}
              </p>
            )}
          </div>
        );

      case "textarea":
        return (
          <div className="group" key={question.id}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.label}
              {question.required && (
                <span className="text-red-400 ml-1 text-xs">*</span>
              )}
            </label>
            <textarea
              value={value || ""}
              onChange={(e) => handleInputChange(question.id, e.target.value)}
              rows={5}
              className={`w-full rounded-xl border-2 ${error ? "border-red-200 bg-red-50/30" : "border-gray-100 bg-gray-50/30"} px-5 py-4 text-base focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400 transition-all duration-200 hover:bg-white resize-none`}
              placeholder="Escreva sua resposta detalhadamente..."
            />
            {error && (
              <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                <AlertCircle size={12} /> {error}
              </p>
            )}
          </div>
        );

      case "number":
        return (
          <div className="group" key={question.id}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.label}
              {question.required && (
                <span className="text-red-400 ml-1 text-xs">*</span>
              )}
            </label>
            <div className="relative">
              <input
                type="number"
                value={value || ""}
                onChange={(e) => handleInputChange(question.id, e.target.value)}
                className={`w-full rounded-xl border-2 ${error ? "border-red-200 bg-red-50/30" : "border-gray-100 bg-gray-50/30"} px-5 py-4 text-base focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400 transition-all duration-200 hover:bg-white`}
                placeholder="Digite um número..."
              />
            </div>
            {error && (
              <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                <AlertCircle size={12} /> {error}
              </p>
            )}
          </div>
        );

      case "radio":
        return (
          <div className="space-y-4" key={question.id}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.label}
              {question.required && (
                <span className="text-red-400 ml-1 text-xs">*</span>
              )}
            </label>
            <div className="grid grid-cols-1 gap-3">
              {question.options?.map((option) => (
                <label
                  key={option.id}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer ${value === option.id ? "border-emerald-400 bg-emerald-50/30 shadow-sm" : "border-gray-100 bg-gray-50/30 hover:bg-gray-100/50"}`}
                >
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={option.id}
                    checked={value === option.id}
                    onChange={(e) =>
                      handleInputChange(question.id, parseInt(e.target.value, 10))
                    }
                    className="w-5 h-5 text-emerald-500 focus:ring-emerald-400 focus:ring-offset-0"
                  />
                  <span className="text-base text-gray-700">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
            {error && (
              <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                <AlertCircle size={12} /> {error}
              </p>
            )}
          </div>
        );

      case "checkbox":
        return (
          <div className="space-y-4" key={question.id}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.label}
              {question.required && (
                <span className="text-red-400 ml-1 text-xs">*</span>
              )}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {question.options?.map((option) => (
                <label
                  key={option.id}
                  className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer ${(value || []).includes(option.id) ? "border-emerald-400 bg-emerald-50/30" : "border-gray-100 bg-gray-50/30 hover:bg-gray-100/50"}`}
                >
                  <input
                    type="checkbox"
                    checked={(value || []).includes(option.id)}
                    onChange={(e) =>
                      handleCheckboxChange(
                        question.id,
                        option.id,
                        e.target.checked,
                      )
                    }
                    className="w-5 h-5 text-emerald-500 rounded focus:ring-emerald-400 focus:ring-offset-0"
                  />
                  <span className="text-base text-gray-700">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
            {error && (
              <p className="text-xs text-red-400 mt-2 flex items-center gap-1">
                <AlertCircle size={12} /> {error}
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mt-4 font-light">
            A preparar o seu inquérito...
          </p>
        </div>
      </div>
    );
  }

  if (totalPages === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            Nenhuma pergunta disponível
          </h3>
          <p className="text-gray-500">
            Este formulário não contém perguntas ou todas estão ocultas devido às condições.
          </p>
          <button
            onClick={onCancel || (() => navigate(-1))}
            className="mt-6 inline-flex items-center gap-2 bg-emerald-600 text-white px-6 py-2 rounded-lg"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {draftModals}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">{draftBanner}</div>
        <div className="mb-10">
          {/* <button
            onClick={onCancel || (() => navigate(-1))}
            className="group inline-flex items-center gap-2 text-gray-400 hover:text-emerald-500 transition-all duration-200 text-sm mb-6"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Voltar
          </button> */}
        </div>

        {/* Progresso */}
        {totalPages > 0 && (
          <div className="max-w-4xl mx-auto mb-10 order-2 border-2 border-black-200 rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-medium text-gray-500">Progresso</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-light text-emerald-500">
                  {Math.round(progress)}%
                </span>
                <span className="text-sm text-gray-400">concluído</span>
              </div>
            </div>
            <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-500 ease-out shadow-lg shadow-emerald-200"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-400">
              <span>Passo {currentPage + 1}</span>
              <span>de {totalPages}</span>
            </div>
          </div>
        )}

        {/* Form Card */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border-2 border-black-200 overflow-hidden">
            {/* Cabeçalho */}
            <div className="bg-gradient-to-r from-gray-50/80 to-white px-8 py-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-800">
                  {currentSection?.title || "Perguntas"}
                </h2>
              </div>
            </div>

            {/* Conteúdo */}
            <div className="px-8 py-8">
              <div className="space-y-8">
                {currentSection?.questions.map((question, idx) => (
                  <div
                    key={question.id}
                    className="animate-fadeInUp"
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    {renderQuestion(question)}
                  </div>
                ))}
              </div>
            </div>

            {/* Navegação */}
            <div className="bg-gray-50/50 px-8 py-6 border-t border-gray-100 flex justify-between items-center">
              <button
                onClick={prevPage}
                disabled={currentPage === 0}
                className={`group inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                  currentPage === 0
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-600 hover:text-emerald-600 hover:bg-gray-100"
                }`}
              >
                <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                Anterior
              </button>

              {currentPage === totalPages - 1 ? (
                <button
                  onClick={handleSubmitClick}
                  disabled={submitting}
                  className="group relative overflow-hidden inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-8 py-3 rounded-xl text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {isUpdate ? "Actualizando..." : "Enviando..."}
                      </>
                    ) : (
                      <>
                        {isUpdate ? (
                          <Save className="w-4 h-4" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        {isUpdate ? "Actualizar" : "Submeter"}
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                </button>
              ) : (
                <button
                  onClick={nextPage}
                  className="group inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-xl text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Próximo
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              )}
            </div>
          </div>
        </div>

       
      </div>

      {/* Modal de Confirmação */}
      <Modal
        title={
          <div className="flex items-center gap-3 text-lg">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            <span>Confirmar Submissão</span>
          </div>
        }
        open={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        footer={[
          <button
            key="cancel"
            onClick={() => setShowConfirmModal(false)}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancelar
          </button>,
          <button
            key="submit"
            onClick={submitForm}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer rounded-lg text-sm font-medium transition-colors"
          >
            Confirmar e Enviar
          </button>,
        ]}
        className="confirm-modal"
      >
        <div className="py-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
            <p className="text-orange-700 text-sm">
              <strong>Atenção!</strong> Após confirmar a submissão, você não poderá mais editar suas respostas.
            </p>
          </div>
          <p className="text-gray-600 mb-2">
            Por favor, revise cuidadosamente todas as suas respostas antes de prosseguir.
          </p>
          <ul className="text-sm text-gray-500 space-y-1 mt-3">
            <li>✓ Certifique-se de que todas as respostas estão corretas</li>
            <li>✓ Após o envio, não será possível fazer alterações</li>
            <li>✓ Suas respostas serão analisadas para melhorar nossos serviços</li>
          </ul>
        </div>
      </Modal>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.5s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default GraduateFormPage;